import { useEffect, useRef, useState, type VideoHTMLAttributes } from 'react';
import { registerGestureUnlock } from '../lib/gestureUnlock';
import { applyIosVideoAttrs } from '../lib/videoAttrs';

type AutoplayVideoProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'autoPlay' | 'muted' | 'loop' | 'playsInline' | 'src'
> & {
  src: string;
};

export function AutoplayVideo({ src, className, ...rest }: AutoplayVideoProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    registerGestureUnlock();
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || shouldLoad) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '200px 0px', threshold: 0 },
    );

    observer.observe(wrap);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;

    applyIosVideoAttrs(video);
    video.src = src;
    video.load();

    const onCanPlay = () => {
      applyIosVideoAttrs(video);
      void video.play().catch(() => {});
    };

    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) onCanPlay();

    return () => {
      video.removeEventListener('canplay', onCanPlay);
      video.removeAttribute('src');
      video.load();
    };
  }, [src, shouldLoad]);

  return (
    <div ref={wrapRef} className={className}>
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        autoPlay
        disablePictureInPicture
        disableRemotePlayback
        className="block h-full w-full object-cover"
        {...rest}
        preload="none"
      />
    </div>
  );
}
